/*
 * A hub's own page (/hubs/bank, /hubs/experiments, /hubs/ai, /hubs/admin;
 * docs/UI_HUBS_AND_GESTURES_PLAN.md 1.1): the hub's title, its sections as
 * headed groups of tiles, each tile a page this person may open with one line
 * on what it is for, and a quick search that narrows the tiles.
 *
 * Which sections and pages show comes from utils/hubs/registry.js, the same
 * data as the menus and the route guard, so a tile never leads to a page the
 * guard would refuse. App.js wraps each route in <Guarded>, so a person who
 * may not open the hub sees NotAllowed instead of this.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { descriptionKey, visibleHubs } from '../../utils/hubs/registry';
import { searchLinks } from '../../utils/hubs/search';

export default function HubPage({ hubKey }) {
  const { t } = useTranslation(['hubs', 'nav']);
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const hub = useMemo(() => visibleHubs(user).find((h) => h.key === hubKey) || null, [user, hubKey]);

  // The tiles a typed query keeps, by path; null while nothing is typed.
  const kept = useMemo(() => {
    if (!hub || !query.trim()) return null;
    return new Set(searchLinks(user, query, t, { hubKey: hub.key }).map((found) => found.item.to));
  }, [hub, user, query, t]);

  if (!hub) return null;   // the guard has already said why
  const title = t(hub.label);
  const sections = hub.sections
    .map((section) => ({ ...section, items: kept ? section.items.filter((item) => kept.has(item.to)) : section.items }))
    .filter((section) => section.items.length > 0);
  const searchId = `hub-search-${hub.key}`;

  return (
    <div className="hubpage" data-testid={`hub-page-${hub.key}`}>
      <header className="hubpage-head">
        <h1 tabIndex={-1}>{title}</h1>
        <p className="hubpage-intro">{t(`${hub.key}.intro`)}</p>
      </header>

      <div className="hubpage-search" role="search">
        <label htmlFor={searchId}>{t('search.inHub', { hub: title })}</label>
        <input id={searchId} type="search" value={query} autoComplete="off" spellCheck={false}
          placeholder={t('search.placeholder')} onChange={(e) => setQuery(e.target.value)}
          data-testid="hub-search" />
      </div>

      {sections.length === 0 && (
        <p className="hubpage-none" role="status" data-testid="hub-search-none">
          {t('search.none', { query: query.trim() })}
        </p>
      )}

      {sections.map((section) => {
        const headingId = `hub-${hub.key}-${section.key}`;
        return (
          <section key={section.key} className="hubpage-section" aria-labelledby={headingId}
            data-testid={`hub-section-${section.key}`}>
            <h2 id={headingId}>{t(section.label)}</h2>
            <ul className="hubpage-tiles">
              {section.items.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="hubpage-tile" data-testid={`hub-tile-${item.key}`}>
                    <span className="hubpage-tile-title">{t(item.label)}</span>
                    <span className="hubpage-tile-desc">{t(descriptionKey(item))}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
